/**
 * The calendar the user is on, sent to the API's current-cycle reads (the Home widgets, the
 * Recorrentes, the top categories) so the server aggregates the month the USER is in — not the
 * month its UTC clock is in. Between 21:00 and midnight on the last day of a month, UTC is already
 * next month while São Paulo is not, and every server-picked "current month" rolled early; the
 * transactions page never had the problem because it sends its own range. Same idea here: the
 * client owns the calendar, the server owns the money.
 *
 * Values are calendar fields (year, 1-based month, `YYYY-MM-DD`), never instants — the server
 * buckets on a Postgres `date` column, so a local instant converted to UTC would shift rows by a
 * day. Mirrors `CalendarScope` in core.api.
 */
export interface CalendarScopeQuery {
  year: number;
  /** 1-based, matching the API's `month` param. */
  month: number;
  /** The user's local calendar date as `YYYY-MM-DD`. */
  today: string;
}

const pad2 = (value: number) => String(value).padStart(2, '0');

/** `YYYY-MM-DD` in LOCAL time — `toISOString()` would already say tomorrow after 21:00 in UTC-3. */
export function localCalendarDate(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

/** Year, month and today derived from ONE instant, so they always agree (the server rejects a
 * `today` outside `year`/`month`). Call it at fetch time, not at render time. */
export function calendarScopeQuery(now: Date = new Date()): CalendarScopeQuery {
  return { year: now.getFullYear(), month: now.getMonth() + 1, today: localCalendarDate(now) };
}

/** Adds the scope's `year`, `month` and `today` to a query; nothing when no scope is given. */
export function appendCalendarScope(params: URLSearchParams, scope?: CalendarScopeQuery): void {
  if (!scope) return;
  params.set('year', String(scope.year));
  params.set('month', String(scope.month));
  params.set('today', scope.today);
}

/** `?year=…&month=…&today=…`, or `''` when no scope is given (the server then uses UTC). */
export function calendarScopeSearch(scope?: CalendarScopeQuery): string {
  if (!scope) return '';
  const params = new URLSearchParams();
  appendCalendarScope(params, scope);
  return `?${params.toString()}`;
}
